import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import { Text } from '@mxenabled/mxui'
import { useTheme } from '@mui/material/styles'

import { CategoryIconLabel } from './CategoryIconLabel'
import { CurrencyText } from '../../CurrencyText'

type DataPoint = { x: string | number; y: number }

const CustomAreaTooltip: React.FC<{
  categoryGuids: { guid: string; topLevelGuid: string }
  hoveredAreaData: DataPoint[]
  cursorPosition: { x: number; y: number }
  chartContainerRef: React.RefObject<HTMLDivElement | null>
}> = ({
  categoryGuids: { guid, topLevelGuid },
  hoveredAreaData,
  cursorPosition,
  chartContainerRef,
}) => {
  const theme = useTheme()
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [adjustedPos, setAdjustedPos] = useState({ x: cursorPosition.x, y: cursorPosition.y })

  useEffect(() => {
    const adjustTooltipPosition = () => {
      const tooltipEl = tooltipRef.current
      const chartEl = chartContainerRef.current

      if (!tooltipEl || !chartEl) return

      const tooltipRect = tooltipEl.getBoundingClientRect()
      const containerRect = chartEl.getBoundingClientRect()

      let newX = cursorPosition.x
      let newY = cursorPosition.y

      // If the tooltip overflows the right or left edge
      if (newX + tooltipRect.width * 2 > containerRect.right) {
        // Move the tooltip to the left of the cursor
        newX = cursorPosition.x - tooltipRect.width
      } else if (newX - tooltipRect.width < containerRect.left) {
        // Move the tooltip to the right of the cursor
        newX = cursorPosition.x
      }

      // If the tooltip overflows the bottom edge
      if (newY + tooltipRect.height * 2 > containerRect.bottom) {
        // Move the tooltip to the top of the cursor
        newY = cursorPosition.y - tooltipRect.height
      }

      setAdjustedPos({ x: newX, y: newY })
    }

    adjustTooltipPosition()
  }, [cursorPosition, chartContainerRef])

  if (!hoveredAreaData) return null

  const seriesSum = hoveredAreaData.reduce((sum, dataPoint) => sum + dataPoint.y, 0)

  return (
    <Card
      ref={tooltipRef}
      sx={{
        position: 'absolute',
        top: adjustedPos.y,
        left: adjustedPos.x,
        width: 250,
        pointerEvents: 'none',
      }}
    >
      <CardContent>
        <Box alignItems="center" display="flex" flexDirection="column">
          <CategoryIconLabel categoryGuid={guid} topLevelCategoryGuid={topLevelGuid} />
          <Text variant="body2">{`${hoveredAreaData.length}-month total`}</Text>
          <CurrencyText amount={seriesSum} bold={true} formatString="0,0" variant="body2" />
        </Box>

        <Divider sx={{ my: 16, width: '100%' }} />

        <Grid container={true}>
          {hoveredAreaData.map((dataPoint, index) => (
            <React.Fragment key={index}>
              {index > 0 && index % 3 === 0 && <Divider sx={{ my: 16, width: '100%' }} />}
              <Grid size={4}>
                <Box alignItems="center" display="flex" flexDirection="column">
                  <Text color={theme.palette.text.secondary} variant="caption">
                    {dataPoint.x}
                  </Text>
                  <CurrencyText
                    amount={dataPoint.y}
                    bold={true}
                    formatString="0,0"
                    variant="body1"
                  />
                </Box>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </CardContent>
    </Card>
  )
}

export default observer(CustomAreaTooltip)
